import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, CircularProgress } from '@mui/material';
import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportMerchantOfferSummaryCreate } from '../../../../DataProvider';
import type { MerchantsOfferSummariesResource } from '../../../../Entities/MerchantsOfferSummary';
import { getJsonApiErrorDetail } from '../../../../utils';
import { getMerchantOfferSummaryReportParams, type Filter } from './utils';

export interface PreviewButtonProps {
  onPreviewStart: () => void;
  onPreviewSuccess: (records: MerchantsOfferSummariesResource[]) => void;
}

export default function PreviewButton({
  onPreviewStart,
  onPreviewSuccess,
}: PreviewButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportMerchantOfferSummaryCreate();

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const merchantsOfferSummariesListAccess = useCanAccess({
    resource: 'merchants_offer_summaries',
    action: 'list',
  });

  const handlePreview = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    onPreviewStart();

    try {
      const result = await runReport(getMerchantOfferSummaryReportParams(values as Filter));
      onPreviewSuccess(result.data);
    } catch (error) {
      notify(getJsonApiErrorDetail(error, 'Preview failed'), { type: 'error' });
    }
  });

  if (!reportsCreateAccess.canAccess || !merchantsOfferSummariesListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      onClick={() => void handlePreview()}
      disabled={report.isPending}
      startIcon={
        report.isPending ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />
      }
    >
      {report.isPending ? 'Loading…' : 'Preview'}
    </Button>
  );
}
